import { Dispatch, SetStateAction, useState } from 'react';

/**
 * Adjust LOCAL component state when a prop changes,
 * using a render-phase state update.
 *
 * This hook is meant ONLY to keep local state in sync with a prop
 * without causing an extra render with stale UI.
 *
 * DO:
 *  - reset or adjust this component's own state
 *  - pass ONLY a React state setter (setState)
 *
 * DO NOT:
 *  - trigger side effects (fetch, analytics, navigation, timers)
 *  - update parent state, context, or external stores
 *
 * If you need to perform side effects or notify external systems,
 * use useEffect instead.
 *
 * React pattern: "Adjusting state when props change"
 * @see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
 */
export const useAdjustStateOnPropChange = <T>(
  prop: T,
  state: T,
  setState: Dispatch<SetStateAction<T>>,
  shouldSync: (nextProp: T, previousProp: T, state: T) => boolean = () => true,
) => {
  const [previousProp, setPreviousProp] = useState(prop);

  const propHasChanged = prop !== previousProp;
  const stateIsOutOfSync = prop !== state;

  if (propHasChanged) {
    setPreviousProp(prop);

    if (stateIsOutOfSync && shouldSync(prop, previousProp, state)) {
      setState(prop);
    }
  }
};
